import type {
  InventoryResource,
  RestoreAccount,
  Snapshot,
} from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { ManagedDatabaseConfiguration } from './managedDatabaseConfiguration';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { Encryption } from '../encryption/encryptionComponent';
import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreRDSState {
  resourceId: string;
  snapshot: Snapshot;
  resourceName?: string;
  supportedCloudProviders: Provider[];
  restoreAccount?: RestoreAccount;
  regionName?: string;
  securityGroupIds?: string[];
  rdsInstanceClass?: string;
  rdsSubnetGroupName?: string;
  vpc?: string;
  initialTags: Record<string, string>;
  tags: Record<string, string>;
  keepOriginalTags: boolean;
  encryption?: Encryption;
  initialRegionName: string;
  databaseFilter?: string[];
  restoreMode?: 'instance' | 'databases';
}

const restoreRDSFlow: Step<RestoreRDSState> = {
  id: 'select_target_account_step',
  Component: SelectTargetAccountStep,
  next: [
    {
      id: 'rds_configuration',
      Component: ManagedDatabaseConfiguration,
    },
  ],
};

export const RestoreRDSWizard = ({
  resource,
  snapshot,
  onClose,
  onBack,
}: {
  resource: InventoryResource;
  snapshot: Snapshot;
  onClose: () => void;
  onBack: () => void;
}) => {
  const { RdsRestoreWithoutEncryptionEnabled } = useFeatureFlags();
  const { isDemo, isDev } = useEnvironment();

  return (
    <Wizard<RestoreRDSState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreRDSFlow]}
      initialState={{
        initialRegionName: resource.region || 'us-east-1',
        snapshot,
        resourceId: snapshot.resourceId,
        supportedCloudProviders:
          isDemo || isDev
            ? [Provider.Aws, Provider.Azure, Provider.Gcp]
            : [Provider.Aws],
        keepOriginalTags: true,
        initialTags: snapshot.resource?.tags || {},
        tags: snapshot.resource?.tags || {},
        encryption: {
          isEncrypted: !RdsRestoreWithoutEncryptionEnabled,
          encryptionKeyId: undefined,
          arn: undefined,
          mode: 'select',
        },
      }}
      title={`Restore RDS`}
      stepperLabels={['Restore Account', 'Configuration']}
    />
  );
};
